import React, { useCallback, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { Option, Options } from '@atlassian/jira-issue-field-select-base/src/common/types.tsx';
import { SearchableSelect } from '@atlassian/jira-searchable-select/src/ui/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';
import {
	transformIssueSuggestionItems,
	stripUrlToIssueKey,
	getCurrentHostPattern,
	stripQueryValueToIssueKey,
} from './utils.tsx';

const getAriaLabelAndPlaceholder = ({
	isJSMProject,
	formatMessage,
}: {
	isJSMProject: boolean;
	formatMessage: ReturnType<typeof useIntl>['formatMessage'];
}) => {
	if (
		!isJSMProject &&
		expVal(
			'enhanced_multiselect_linking_experiment',
			'isEnhanceMultiSelectLinkedIssueEnable',
			false,
		)
	) {
		const msg = messages.enhanceMultiSelectLinkedIssueEnableText;
		const searchMessage = messages.searchMessage;
		return {
			placeholder: formatMessage(searchMessage),
			ariaLabel: formatMessage(msg),
		};
	}

	if (fg('jira-issue-terminology-refresh-m3')) {
		return {
			ariaLabel: formatMessage(messages.placeholderIssueTermRefresh),
			placeholder: formatMessage(messages.placeholderIssueTermRefresh),
		};
	}

	return {
		ariaLabel: formatMessage(messages.placeholder),
		placeholder: formatMessage(messages.placeholder),
	};
};

const IssueLinksSearchEdit = (props: Props) => {
	const { formatMessage } = useIntl();
	const [invalidSelections, setInValidSelections] = useState<unknown>([]);
	const [isInvalidState, setIsInvalidState] = useState(false);

	const {
		fieldId,
		onFocus,
		onBlur,
		value,
		options,
		onChange,
		autoCompleteUrl,
		loadingMessage,
		getSuggestions,
		useTransformSuggestions,
		noOptionsMessage,
		fetchSuggestionsOnMount,
		isInvalid,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		isDisabled,
	} = props;

	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const isJSMProject = projectType === SERVICE_DESK_PROJECT;

	const { ariaLabel, placeholder } = getAriaLabelAndPlaceholder({
		isJSMProject,
		formatMessage,
	});

	const fetchSuggestions = useCallback(
		async (query: string) => {
			const response = await performGetRequest(`${autoCompleteUrl}${query}`);
			return response;
		},
		[autoCompleteUrl],
	);

	/** returns the update invalid selection list that only contains values still present in the user selections */
	const updateInvalidSelections = useCallback(
		(selectionValues: Option[]): void => {
			const issueKeyValues = selectionValues ? selectionValues.map((v) => v.value) : [];
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const updateSelections = invalidSelections.length
				? // @ts-expect-error - TS2571 - Object is of type 'unknown'. | TS7006 - Parameter 'i' implicitly has an 'any' type.
					invalidSelections.filter((i) => issueKeyValues.includes(i))
				: [];
			if (updateSelections.length) {
				setIsInvalidState(true);
			} else {
				setIsInvalidState(false);
			}
			setInValidSelections(updateSelections);
		},
		[invalidSelections],
	);

	const onChangeSelectedValues = useCallback(
		(values: Option[], suggestions?: Options): void => {
			const currentHost = getCurrentHostPattern();
			updateInvalidSelections(values);
			const newIssueLinks =
				values !== null && values !== undefined
					? values.map((issueLink) => {
							const strippedIssueKey = stripUrlToIssueKey(currentHost, issueLink.value.toString());

							return {
								...issueLink,
								iconUrl:
									issueLink.__isNew__ === true && suggestions && suggestions[0].options
										? suggestions[0].options.find((option) => option.value === issueLink.value)
												?.iconUrl
										: issueLink.iconUrl,
								label: strippedIssueKey,
								value: strippedIssueKey,
							};
						})
					: values;
			onChange(newIssueLinks);
		},
		[onChange, updateInvalidSelections],
	);

	return (
		<FlexRow>
			<Box xcss={searchWrapperStyles}>
				<SearchableSelect
					fieldId={fieldId}
					getSuggestions={getSuggestions !== undefined ? getSuggestions : fetchSuggestions}
					onChange={onChangeSelectedValues}
					isMulti
					value={value}
					options={options}
					onBlur={onBlur}
					onFocus={onFocus}
					onCloseMenuOnScroll={onCloseMenuOnScroll}
					isDropdownMenuFixedAndLayered={isDropdownMenuFixedAndLayered}
					isDisabled={isDisabled}
					placeholder={placeholder}
					getTransformedSuggestions={
						useTransformSuggestions !== undefined
							? useTransformSuggestions
							: transformIssueSuggestionItems
					}
					canCreateNewItem
					loadingMessage={loadingMessage}
					noOptionsMessage={noOptionsMessage}
					onChangeQueryValue={stripQueryValueToIssueKey}
					isInvalid={isInvalid === true || isInvalidState}
					fetchSuggestionsOnMount={fetchSuggestionsOnMount}
					formatCreateLabel={formatMessage(messages.createNewIssueLink)}
					ariaLabel={ariaLabel}
				/>
			</Box>
		</FlexRow>
	);
};

export default IssueLinksSearchEdit;

const searchWrapperStyles = xcss({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexRow = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	flex: 1,
});
